// VerantwortungDatenDialog.tsx — Popup „Verantwortung fürs Unternehmen" einer
// gespeicherten Person in der Akte einer Firma (User 30.09.2026).
//
// Öffnet sich per Klick auf die ROLLE (der Name öffnet weiter die Akte der
// Person). Erst ansehen; „Daten ändern" schaltet die unternehmensbezogenen
// Daten frei — auch bei geschlossenem Schloss, sofern Schreibrecht besteht:
//   Rolle · geschäftliches Telefon · geschäftliche E-Mail · Abteilung.
// Telefon und E-Mail sind immer die geschäftlichen Wege der Person MIT
// Firmenbezug zu dieser Firma (fehlt einer, wird er neu angelegt; eine andere
// Nummer der Person wird nie überschrieben).
//
// Gespeichert wird sofort, danach lädt die Akte neu. Hat das Formular
// ungespeicherte Änderungen, wird nicht gespeichert (akteSofortSpeichern.ts).

import * as React from "react";
import { pruefeTelefon, telefonVerlassen } from "@kmvs/forms-core";
import { REFINED } from "../risikoanalyseStep/tokens";
import { EintragDialog } from "../forms/EintragDialog";
import { TelefonEingabe } from "../forms/widgets/TelefonEingabe";
import { VerantwortlichkeitInput, type VerantwortlichkeitValue } from "../verantwortlichkeit/VerantwortlichkeitInput";
import { buildMenschenItems } from "../verantwortlichkeit/buildMenschenItems";
import { attachMenschen } from "../../services/menschenFuersUnternehmenService";
import { firmenKommunikationSpeichern, sofortFehlerText } from "../../services/firmenKommunikationService";
import { UNGESPEICHERT_HINWEIS, type AkteSofortSpeichern } from "./akteSofortSpeichern";
import type { MitarbeiterAbsicherung } from "./menschenZusatzSpalten";
import type { MenschEntry } from "./MenschenFuersUnternehmenList";
import { refName } from "./beziehungsAnzeige";
import { hinweisen } from "../redesign/bestaetigen";

const MAIL_OK = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const labelStil: React.CSSProperties = {
  fontSize: "0.78rem",
  fontWeight: 700,
  letterSpacing: "0.02em",
  color: REFINED.ink2,
};
const feldStil: React.CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  font: "inherit",
  fontSize: "0.92rem",
  padding: "8px 10px",
  border: `1.5px solid ${REFINED.line}`,
  borderRadius: 9,
  background: REFINED.white,
  color: REFINED.ink,
};

export function VerantwortungDatenDialog({
  titel,
  eintrag,
  firmaId,
  legalFormId,
  zentrale,
  abs,
  sofort,
  startModus,
  onSchliessen,
}: {
  titel: string;
  eintrag: MenschEntry;
  firmaId: number;
  legalFormId?: number;
  zentrale?: readonly (string | null | undefined)[];
  abs: MitarbeiterAbsicherung;
  sofort: AkteSofortSpeichern;
  startModus: "ansehen" | "aendern";
  onSchliessen: () => void;
}) {
  const personId = eintrag.ref?.contactId ?? 0;
  const komm = eintrag.ref?.kommunikation;
  const telAlt = komm?.telefon ?? "";
  const mailAlt = komm?.email ?? "";
  const zeile = abs.aktiv ? abs.zeile(personId) : null;
  const abtAlt = zeile?.department ?? "";

  const [modus, setModus] = React.useState<"ansehen" | "aendern">(
    startModus === "aendern" && sofort.darfSchreiben && !sofort.hatUngespeicherte() ? "aendern" : "ansehen",
  );
  const [hinweis, setHinweis] = React.useState<string | null>(
    startModus === "aendern" && sofort.darfSchreiben && sofort.hatUngespeicherte() ? UNGESPEICHERT_HINWEIS : null,
  );
  const [verant, setVerant] = React.useState<VerantwortlichkeitValue | null>(eintrag.verant);
  const [tel, setTel] = React.useState(telAlt);
  const [mail, setMail] = React.useState(mailAlt);
  const [abt, setAbt] = React.useState(abtAlt);
  const [fehler, setFehler] = React.useState<string | null>(null);
  const [speichert, setSpeichert] = React.useState(false);

  // Die Abteilung lädt nach — solange nichts eingetippt ist, den Bestand zeigen.
  const abtBearbeitet = React.useRef(false);
  React.useEffect(() => {
    if (!abtBearbeitet.current) setAbt(abtAlt);
  }, [abtAlt]);

  const datenAendern = () => {
    if (sofort.hatUngespeicherte()) {
      setHinweis(UNGESPEICHERT_HINWEIS);
      return;
    }
    setHinweis(null);
    setModus("aendern");
  };

  const speichern = async () => {
    if (sofort.hatUngespeicherte()) {
      setFehler(UNGESPEICHERT_HINWEIS);
      return;
    }
    const telNeu = tel.trim() ? telefonVerlassen(tel, { geschaeftlich: true, zentrale }) : "";
    const telFehler = telNeu ? pruefeTelefon(telNeu) : undefined;
    if (telFehler) {
      setFehler(`Telefon: ${telFehler}`);
      return;
    }
    const mailNeu = mail.trim();
    if (mailNeu && !MAIL_OK.test(mailNeu)) {
      setFehler("Bitte eine gültige E-Mail-Adresse eingeben.");
      return;
    }
    const rolleGeaendert = JSON.stringify(verant) !== JSON.stringify(eintrag.verant);
    const items = rolleGeaendert ? buildMenschenItems([{ ...eintrag, verant }], firmaId) : [];
    if (rolleGeaendert && !items.length) {
      setFehler("Bitte mindestens eine Rolle wählen.");
      return;
    }

    // Reihenfolge: Rolle, Telefon, E-Mail, Abteilung. Ein leer gemachtes Feld
    // löscht nichts — es bleibt beim Bestand.
    const schritte: Array<{ was: string; los: () => Promise<unknown> }> = [];
    if (items.length) schritte.push({ was: "Die Rolle", los: () => attachMenschen(items, true) });
    if (telNeu && telNeu !== telAlt) {
      schritte.push({
        was: "Das Telefon",
        los: () =>
          firmenKommunikationSpeichern({ personId, firmaId, typ: "phone", wert: telNeu, vorhandenId: komm?.telefonId }),
      });
    }
    if (mailNeu && mailNeu !== mailAlt.trim()) {
      schritte.push({
        was: "Die E-Mail",
        los: () =>
          firmenKommunikationSpeichern({ personId, firmaId, typ: "email", wert: mailNeu, vorhandenId: komm?.emailId }),
      });
    }
    if (zeile && abt.trim() !== abtAlt) {
      schritte.push({ was: "Die Abteilung", los: () => abs.speichern({ ...zeile, department: abt.trim() || null }) });
    }
    if (!schritte.length) {
      onSchliessen();
      return;
    }

    setSpeichert(true);
    setFehler(null);
    const fehlgeschlagen: string[] = [];
    let erfolgreich = 0;
    for (const s of schritte) {
      try {
        await s.los();
        erfolgreich++;
      } catch (e) {
        fehlgeschlagen.push(sofortFehlerText(e, s.was));
      }
    }
    setSpeichert(false);
    if (fehlgeschlagen.length && !erfolgreich) {
      setFehler(fehlgeschlagen.join(" "));
      return;
    }
    if (fehlgeschlagen.length) {
      await hinweisen({ titel: "Nicht alles gespeichert", text: "Gespeichert, aber:\n\n" + fehlgeschlagen.join("\n") });
    }
    onSchliessen();
    sofort.neuLaden();
  };

  const name = refName(eintrag.ref) || "Ohne Namen";
  const aendern = modus === "aendern";

  const personZeile = (
    <div style={{ display: "flex", flexDirection: "column", rowGap: 2 }}>
      <span style={labelStil}>Person</span>
      <span style={{ fontWeight: 700 }}>{name}</span>
    </div>
  );
  const rolle = (
    <VerantwortlichkeitInput
      idBase={`vdd-${eintrag.id}-v`}
      value={aendern ? verant : eintrag.verant}
      onChange={(v) => aendern && setVerant(v)}
      legalFormId={legalFormId}
      gegenkontaktIstFirma={false}
    />
  );

  if (!aendern) {
    const wert = (v: string) => (v ? v : <span style={{ color: REFINED.ink3 }}>—</span>);
    return (
      <EintragDialog
        titel={titel}
        nurAnsehen
        ansehenHinweis={sofort.darfSchreiben ? null : "Nur ansehen — keine Schreibrechte an dieser Akte."}
        onUebernehmen={() => undefined}
        onAbbrechen={onSchliessen}
        fussExtra={
          sofort.darfSchreiben ? (
            <button
              type="button"
              onClick={datenAendern}
              style={{
                appearance: "none",
                borderRadius: 10,
                padding: "9px 16px",
                fontFamily: REFINED.fontUI,
                fontWeight: 600,
                fontSize: "0.88rem",
                cursor: "pointer",
                border: `1.5px solid ${REFINED.aub}`,
                background: REFINED.white,
                color: REFINED.aub700,
                marginRight: "auto",
              }}
            >
              Daten ändern
            </button>
          ) : undefined
        }
      >
        {hinweis && (
          <div role="alert" style={{ color: REFINED.err, fontSize: "0.82rem" }}>
            {hinweis}
          </div>
        )}
        {personZeile}
        {rolle}
        <dl style={{ display: "grid", gridTemplateColumns: "auto 1fr", columnGap: 14, rowGap: 6, margin: 0 }}>
          <dt style={labelStil}>Geschäftliches Telefon</dt>
          <dd style={{ margin: 0 }}>{wert(telAlt)}</dd>
          <dt style={labelStil}>Geschäftliche E-Mail</dt>
          <dd style={{ margin: 0 }}>{wert(mailAlt)}</dd>
          {zeile && (
            <>
              <dt style={labelStil}>Abteilung</dt>
              <dd style={{ margin: 0 }}>{wert(abtAlt)}</dd>
            </>
          )}
        </dl>
      </EintragDialog>
    );
  }

  return (
    <EintragDialog
      titel={titel}
      kicker="Daten ändern"
      uebernehmenText="Speichern"
      beschaeftigt={speichert}
      fehler={fehler}
      onUebernehmen={() => void speichern()}
      onAbbrechen={onSchliessen}
    >
      <div role="note" style={{ fontSize: "0.82rem", color: REFINED.ink2, fontStyle: "italic" }}>
        Wird sofort gespeichert. Telefon und E-Mail gelten geschäftlich für dieses Unternehmen.
      </div>
      {personZeile}
      {rolle}
      <label style={{ display: "flex", flexDirection: "column", rowGap: 4 }}>
        <span style={labelStil}>Geschäftliches Telefon</span>
        <TelefonEingabe
          value={tel}
          onChange={setTel}
          geschaeftlich
          zentrale={zentrale}
          aria-label="Geschäftliches Telefon"
          placeholder="+49 …"
          style={feldStil}
        />
      </label>
      <label style={{ display: "flex", flexDirection: "column", rowGap: 4 }}>
        <span style={labelStil}>Geschäftliche E-Mail</span>
        <input
          type="email"
          value={mail}
          aria-label="Geschäftliche E-Mail"
          placeholder="name@firma.de"
          onChange={(e) => setMail(e.target.value)}
          style={feldStil}
        />
      </label>
      {zeile && (
        <label style={{ display: "flex", flexDirection: "column", rowGap: 4 }}>
          <span style={labelStil}>Abteilung</span>
          <input
            type="text"
            value={abt}
            aria-label="Abteilung"
            onChange={(e) => {
              abtBearbeitet.current = true;
              setAbt(e.target.value);
            }}
            style={feldStil}
          />
        </label>
      )}
    </EintragDialog>
  );
}
